/* eslint-disable curly, multiline-ternary */
import { HamburgerIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Img,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Progress,
  Text,
  Tooltip,
  useDisclosure,
  useMediaQuery
} from '@chakra-ui/react'
import {
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChevronDoubleLeftIcon,
  Cog6ToothIcon,
  GiftIcon,
  HomeIcon,
  QueueListIcon
} from '@heroicons/react/24/outline'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'
import { ReactSearchAutocomplete } from 'react-search-autocomplete'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useAuthState } from '../../auth/AuthProvider'
import useAuth from '../../auth/hooks/useAuth'
import { useAccount } from '../../client/modules/account/AccountProvider'
import { type Patient } from '../../model/patient'
import { type QueueEntryType } from '../../model/queue'
import { setCurrentPatient } from '../../reducers/currentPatientReducer'
import { setVisits } from '../../reducers/currentVisitsReducer'
import { getPatientService, getQueueService } from '../../services/Services'
import { useAppDispatch } from '../../store/hooks'
import SidebarQueue from './SidebarQueue'

const SIDEBAR_STORAGE_KEY = 'sijilo:sidebar-collapsed'

type ShellRole = 'doctor' | 'secretary'
type ContentMode = 'default' | 'wide' | 'full' | 'workspace'

interface InternalAppShellProps {
  children: ReactNode
  role?: ShellRole
  title?: ReactNode
  breadcrumb?: ReactNode
  headerActions?: ReactNode
  contentMode?: ContentMode
}

interface NavItem {
  label: string
  href: string
  icon: typeof HomeIcon
  exact?: boolean
}

function readCollapsedState() {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <HStack
      spacing={collapsed ? 0 : 2.5}
      minW={0}
      justify={collapsed ? 'center' : 'flex-start'}
    >
      <Img
        src="/sijilo.png"
        alt={collapsed ? 'Sijilo' : ''}
        h="32px"
        w={collapsed ? '32px' : 'auto'}
        objectFit="contain"
        flex="0 0 auto"
      />
      {!collapsed && (
        <Text
          fontSize="sm"
          fontWeight="700"
          letterSpacing="-0.02em"
          noOfLines={1}
        >
          Sijilo
        </Text>
      )}
    </HStack>
  )
}

function MonthlyUsageMeter({ collapsed }: { collapsed: boolean }) {
  const { formatMessage } = useIntl()
  const {
    accountAccess,
    freeMonthlyLimit,
    hasTimedAccess,
    loading,
    monthlyUsage
  } = useAccount()
  const limit = Number(freeMonthlyLimit)
  const used = Math.max(0, Number(monthlyUsage) || 0)

  if (
    loading ||
    accountAccess?.schemaVersion !== 2 ||
    !Number.isFinite(limit) ||
    limit <= 0
  ) {
    return null
  }

  const remaining = Math.max(0, limit - used)
  const isUnlimited = hasTimedAccess
  const percentage = isUnlimited ? 100 : Math.min(100, (used / limit) * 100)
  const colorScheme = isUnlimited
    ? 'green'
    : percentage >= 100
      ? 'red'
      : percentage >= 80
        ? 'orange'
        : 'blue'
  const title = formatMessage({
    id: 'internalNav.monthlyUsage',
    defaultMessage: 'Monthly usage'
  })
  const count = isUnlimited
    ? formatMessage(
        {
          id: 'internalNav.usageVisits',
          defaultMessage:
            '{used, plural, one {# visit this month} other {# visits this month}}'
        },
        { used }
      )
    : formatMessage(
        {
          id: 'internalNav.usageCount',
          defaultMessage: '{used} / {limit}'
        },
        { used, limit }
      )
  const remainingText = isUnlimited
    ? formatMessage({
        id: 'internalNav.noMonthlyLimit',
        defaultMessage: 'No monthly limit'
      })
    : formatMessage(
        {
          id: 'internalNav.usageRemaining',
          defaultMessage:
            '{remaining, plural, one {# visit remaining} other {# visits remaining}}'
        },
        { remaining }
      )

  if (collapsed) {
    return (
      <Tooltip
        label={
          <Box py={1}>
            <Text fontSize="xs" fontWeight="700">
              {title}
            </Text>
            <Text mt={1} fontSize="xs">
              {count}
            </Text>
            <Text fontSize="xs">{remainingText}</Text>
          </Box>
        }
        placement="right"
        hasArrow
      >
        <Box
          mx="auto"
          mb={3}
          w="32px"
          h="32px"
          tabIndex={0}
          aria-label={`${title}: ${count}; ${remainingText}`}
          data-testid="monthly-usage-indicator"
        >
          <CircularProgress
            value={percentage}
            size="32px"
            thickness="10px"
            color={`${colorScheme}.500`}
            trackColor="gray.200"
          >
            <CircularProgressLabel fontSize="8px" fontWeight="700">
              {isUnlimited ? used : remaining}
            </CircularProgressLabel>
          </CircularProgress>
        </Box>
      </Tooltip>
    )
  }

  return (
    <Box
      mx={2}
      mb={3}
      p={3}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="10px"
      bg="gray.50"
      data-testid="monthly-usage-meter"
    >
      <Flex align="center" justify="space-between" gap={2} mb={2}>
        <Text fontSize="xs" fontWeight="650" color="gray.700">
          {title}
        </Text>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
          {count}
        </Text>
      </Flex>
      {!isUnlimited && (
        <Progress
          value={percentage}
          size="sm"
          colorScheme={colorScheme}
          borderRadius="full"
          bg="gray.200"
          aria-label={title}
        />
      )}
      <Text
        mt={isUnlimited ? 0 : 2}
        fontSize="xs"
        color={
          isUnlimited ? 'green.600' : remaining === 0 ? 'red.600' : 'gray.500'
        }
      >
        {remainingText}
      </Text>
    </Box>
  )
}

function SidebarContent({
  role,
  collapsed,
  waitingEntries,
  queueLoading,
  onToggle,
  onNavigate
}: {
  role: ShellRole
  collapsed: boolean
  waitingEntries: QueueEntryType[]
  queueLoading: boolean
  onToggle?: () => void
  onNavigate?: () => void
}) {
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()
  const { logoutUser } = useAuth()

  const doctorNav: NavItem[] = [
    {
      label: formatMessage({
        id: 'internalNav.dashboard',
        defaultMessage: 'Dashboard'
      }),
      href: '/dashboard',
      icon: HomeIcon,
      exact: true
    },
    {
      label: formatMessage({
        id: 'internalNav.calendar',
        defaultMessage: 'Calendar'
      }),
      href: '/calendar',
      icon: CalendarDaysIcon
    },
    {
      label: formatMessage({
        id: 'reports.analytics',
        defaultMessage: 'Analytics'
      }),
      href: '/analytics',
      icon: ChartBarIcon
    },
    {
      label: formatMessage({
        id: 'reports.finance',
        defaultMessage: 'Finances'
      }),
      href: '/finances',
      icon: BanknotesIcon
    },
    {
      label: formatMessage({
        id: 'internalNav.referrals',
        defaultMessage: 'Referrals'
      }),
      href: '/referrals',
      icon: GiftIcon
    }
  ]
  const secretaryNav: NavItem[] = [
    {
      label: formatMessage({
        id: 'reports.collections',
        defaultMessage: 'Collections'
      }),
      href: '/collections',
      icon: QueueListIcon
    },
    {
      label: formatMessage({
        id: 'internalNav.queue',
        defaultMessage: 'Queue'
      }),
      href: '/secretary',
      icon: QueueListIcon,
      exact: true
    },
    {
      label: formatMessage({
        id: 'internalNav.calendar',
        defaultMessage: 'Calendar'
      }),
      href: '/calendar',
      icon: CalendarDaysIcon
    }
  ]
  const nav = role === 'doctor' ? doctorNav : secretaryNav

  const renderNavItem = (item: NavItem) => {
    const active = item.exact
      ? pathname === item.href
      : pathname.startsWith(item.href)
    const content = (
      <Flex
        align="center"
        justify={collapsed ? 'center' : 'flex-start'}
        gap={3}
        h="36px"
        px={collapsed ? 0 : 3}
        borderRadius="8px"
        bg={active ? 'gray.100' : 'transparent'}
        color={active ? 'gray.950' : 'gray.600'}
        fontWeight={active ? 650 : 500}
        fontSize="sm"
        _hover={{ bg: 'gray.100', color: 'gray.950' }}
        transition="background .15s ease, color .15s ease"
      >
        <Icon as={item.icon} boxSize="18px" flex="0 0 auto" />
        {!collapsed && <Text noOfLines={1}>{item.label}</Text>}
      </Flex>
    )

    return (
      <Tooltip
        key={item.href}
        label={collapsed ? item.label : ''}
        placement="right"
      >
        <Box
          as={Link}
          to={item.href}
          display="block"
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          aria-label={collapsed ? item.label : undefined}
        >
          {content}
        </Box>
      </Tooltip>
    )
  }

  return (
    <Flex direction="column" h="full" p={2}>
      <Flex
        h="40px"
        align="center"
        justify={collapsed ? 'center' : 'space-between'}
        px={collapsed ? 0 : 2}
        mb={4}
      >
        <Brand collapsed={collapsed} />
        {!collapsed && onToggle && (
          <IconButton
            aria-label={formatMessage({
              id: 'internalNav.collapse',
              defaultMessage: 'Collapse sidebar'
            })}
            icon={<ChevronDoubleLeftIcon width="16" />}
            size="sm"
            variant="ghost"
            onClick={onToggle}
          />
        )}
      </Flex>

      {role === 'secretary' && !collapsed && (
        <Badge alignSelf="flex-start" mx={2} mb={3} colorScheme="gray">
          {formatMessage({
            id: 'internalNav.secretary',
            defaultMessage: 'Secretary'
          })}
        </Badge>
      )}

      <Flex
        as="nav"
        direction="column"
        gap={1}
        aria-label={formatMessage({
          id: 'internalNav.mainNavigation',
          defaultMessage: 'Main navigation'
        })}
      >
        {nav.map(renderNavItem)}
      </Flex>

      {role === 'doctor' && (
        <SidebarQueue
          entries={waitingEntries}
          collapsed={collapsed}
          loading={queueLoading}
          onNavigate={onNavigate}
        />
      )}

      <Box mt="auto">
        {role === 'doctor' && <MonthlyUsageMeter collapsed={collapsed} />}
        {role === 'doctor' ? (
          renderNavItem({
            label: formatMessage({
              id: 'internalNav.settings',
              defaultMessage: 'Settings'
            }),
            href: '/settings',
            icon: Cog6ToothIcon,
            exact: true
          })
        ) : (
          <Button
            w="full"
            justifyContent={collapsed ? 'center' : 'flex-start'}
            variant="ghost"
            color="gray.600"
            onClick={logoutUser}
          >
            {collapsed
              ? '↪'
              : formatMessage({
                  id: 'navbar.logout',
                  defaultMessage: 'Log out'
                })}
          </Button>
        )}
        {collapsed && onToggle && (
          <Tooltip
            label={formatMessage({
              id: 'internalNav.expand',
              defaultMessage: 'Expand sidebar'
            })}
            placement="right"
          >
            <IconButton
              mt={1}
              w="full"
              aria-label={formatMessage({
                id: 'internalNav.expand',
                defaultMessage: 'Expand sidebar'
              })}
              icon={
                <ChevronDoubleLeftIcon
                  width="16"
                  style={{ transform: 'rotate(180deg)' }}
                />
              }
              size="sm"
              variant="ghost"
              onClick={onToggle}
            />
          </Tooltip>
        )}
      </Box>
    </Flex>
  )
}

export function PatientSearch() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { uid } = useAuthState()
  const { formatMessage } = useIntl()
  const [searchTerm, setSearchTerm] = useState('')
  const [items, setItems] = useState<Patient[]>([])

  const searchPatients = useCallback(async () => {
    if (!uid) return
    const patients = await getPatientService().searchPatients({
      searchTerm,
      ownerId: uid
    })
    setItems(patients)
  }, [searchTerm, uid])

  useEffect(() => {
    void searchPatients()
  }, [searchPatients])

  return (
    <Box
      w="full"
      maxW="420px"
      h="32px"
      sx={{
        '.wrapper': {
          border: '1px solid #e5e5e5',
          borderRadius: '8px',
          boxShadow: 'none',
          minHeight: '32px'
        },
        '.wrapper:focus-within': {
          borderColor: '#171717',
          boxShadow: '0 0 0 1px #171717'
        },
        input: { height: '30px', fontSize: '13px', paddingBlock: 0 },
        '.search-icon': { width: '16px' },
        '.clear-icon': { width: '14px' },
        '.line': { display: 'none' },
        '.wrapper > div + div': {
          bg: 'white',
          borderTop: '1px solid',
          borderColor: 'gray.200',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden'
        },
        '.wrapper > div + div > ul': {
          bg: 'white',
          py: '4px',
          maxH: '280px',
          overflowY: 'auto'
        },
        '.wrapper [data-test="result"], .wrapper [data-test="no-results-message"]':
          {
            minH: '36px',
            px: '10px',
            py: '7px',
            bg: 'white',
            color: 'gray.800',
            cursor: 'pointer'
          },
        '.wrapper [data-test="result"]:hover, .wrapper [data-test="result"].selected':
          {
            bg: 'gray.100'
          },
        '.wrapper .ellipsis': {
          marginInlineStart: '8px',
          marginLeft: 0,
          textAlign: 'start',
          fontSize: '13px'
        }
      }}
    >
      <ReactSearchAutocomplete
        items={items}
        onSearch={setSearchTerm}
        onSelect={(patient: Patient) => {
          dispatch(setCurrentPatient(patient))
          dispatch(setVisits([]))
          navigate(`/patients/${patient.id}`)
        }}
        placeholder={formatMessage({
          id: 'navbar.searchPlaceholder',
          defaultMessage: 'Search patients…'
        })}
        styling={{
          height: '32px',
          border: '1px solid #e5e5e5',
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          hoverBackgroundColor: '#f5f5f5',
          color: '#262626',
          fontSize: '13px',
          iconColor: '#737373',
          lineColor: '#e5e5e5',
          placeholderColor: '#a3a3a3',
          zIndex: 30,
          searchIconMargin: '0 8px 0 10px',
          clearIconMargin: '3px 8px 0 0'
        }}
        showIcon
      />
    </Box>
  )
}

function routeTitle(
  pathname: string,
  formatMessage: ReturnType<typeof useIntl>['formatMessage']
) {
  if (pathname.startsWith('/patients/new'))
    return formatMessage({
      id: 'internalNav.newPatient',
      defaultMessage: 'New patient'
    })
  if (pathname.startsWith('/patients/'))
    return formatMessage({
      id: 'internalNav.patientRecord',
      defaultMessage: 'Patient record'
    })
  if (pathname.startsWith('/calendar'))
    return formatMessage({
      id: 'internalNav.calendar',
      defaultMessage: 'Calendar'
    })
  if (pathname.startsWith('/settings'))
    return formatMessage({
      id: 'internalNav.settings',
      defaultMessage: 'Settings'
    })
  if (pathname.startsWith('/referrals'))
    return formatMessage({
      id: 'internalNav.referrals',
      defaultMessage: 'Referrals'
    })
  if (pathname.startsWith('/secretary'))
    return formatMessage({ id: 'internalNav.queue', defaultMessage: 'Queue' })
  return formatMessage({
    id: 'internalNav.dashboard',
    defaultMessage: 'Dashboard'
  })
}

export default function InternalAppShell({
  children,
  role = 'doctor',
  title,
  breadcrumb,
  headerActions,
  contentMode = 'default'
}: InternalAppShellProps) {
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()
  const { locale, setLocale } = useAccount()
  const { uid, photoURL, fullName } = useAuthState()
  const { logoutUser } = useAuth()
  const drawer = useDisclosure()
  const [storedCollapsed, setStoredCollapsed] = useState(readCollapsedState)
  const [waitingEntries, setWaitingEntries] = useState<QueueEntryType[]>([])
  const [queueLoading, setQueueLoading] = useState(role === 'doctor')
  const [isLg] = useMediaQuery('(min-width: 768px)')
  const [isXl] = useMediaQuery('(min-width: 1280px)')
  const collapsed = isLg && !isXl ? true : storedCollapsed

  const pageTitle = title ?? routeTitle(pathname, formatMessage)
  const maxW = useMemo(() => {
    if (contentMode === 'full') return 'none'
    if (contentMode === 'wide' || contentMode === 'workspace') return '1440px'
    return '1180px'
  }, [contentMode])
  const isWorkspace = contentMode === 'workspace'

  const toggleCollapsed = () => {
    setStoredCollapsed((value) => {
      const next = !value
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      } catch {}
      return next
    })
  }

  useEffect(() => {
    if (role !== 'doctor' || !uid) {
      setWaitingEntries([])
      setQueueLoading(false)
      return
    }

    const queueService = getQueueService()
    if (!queueService) {
      setQueueLoading(false)
      return
    }

    setQueueLoading(true)
    return queueService.getTodayQueue(
      uid,
      (entries) => {
        setWaitingEntries(
          entries
            .filter((entry) => entry.status === 'waiting')
            .sort((a, b) => a.position - b.position)
        )
        setQueueLoading(false)
      },
      () => {
        setWaitingEntries([])
        setQueueLoading(false)
      }
    )
  }, [role, uid])

  return (
    <Flex minH="100vh" bg="gray.50" align="stretch">
      <Box
        as="aside"
        display={{ base: 'none', lg: 'block' }}
        position="sticky"
        top={0}
        h="100vh"
        w={collapsed ? '48px' : '256px'}
        flex={`0 0 ${collapsed ? '48px' : '256px'}`}
        bg="white"
        borderInlineEnd="1px solid"
        borderColor="gray.200"
        transition="width .18s ease, flex-basis .18s ease"
        zIndex={20}
        data-testid="desktop-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
      >
        <SidebarContent
          role={role}
          collapsed={collapsed}
          waitingEntries={waitingEntries}
          queueLoading={queueLoading}
          onToggle={toggleCollapsed}
        />
        <Box
          as="button"
          type="button"
          position="absolute"
          top={0}
          insetInlineEnd={0}
          bottom={0}
          w="3px"
          cursor="col-resize"
          aria-label={formatMessage({
            id: 'internalNav.toggleSidebar',
            defaultMessage: 'Toggle sidebar'
          })}
          onClick={toggleCollapsed}
          _hover={{ bg: 'gray.200' }}
          transition="background .15s ease"
          zIndex={21}
          data-testid="sidebar-toggle-border"
        />
      </Box>

      <Drawer
        isOpen={drawer.isOpen}
        placement={locale === 'ar' ? 'right' : 'left'}
        onClose={drawer.onClose}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent maxW="256px">
          <DrawerBody p={0}>
            <SidebarContent
              role={role}
              collapsed={false}
              waitingEntries={waitingEntries}
              queueLoading={queueLoading}
              onNavigate={drawer.onClose}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Flex direction="column" minW={0} flex={1}>
        <Flex
          as="header"
          position="sticky"
          top={0}
          zIndex={15}
          h="48px"
          minH="48px"
          align="center"
          gap={{ base: 2, md: 4 }}
          px={{ base: 3, md: 5 }}
          bg="rgba(255,255,255,.94)"
          borderBottom="1px solid"
          borderColor="gray.200"
          sx={{ backdropFilter: 'blur(12px)' }}
        >
          <IconButton
            display={{ base: 'inline-flex', lg: 'none' }}
            aria-label={formatMessage({
              id: 'navbar.openMenu',
              defaultMessage: 'Open main menu'
            })}
            icon={<HamburgerIcon />}
            variant="ghost"
            size="sm"
            onClick={drawer.onOpen}
          />
          <Box display={{ base: 'none', md: 'block' }} minW="160px">
            {breadcrumb && (
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {breadcrumb}
              </Text>
            )}
            <Text fontWeight="650" fontSize="sm" noOfLines={1}>
              {pageTitle}
            </Text>
          </Box>

          {role === 'doctor' && (
            <Flex flex={1} justify="center" minW={0}>
              <Box
                w={{ base: 'full', xl: 'clamp(320px, 30vw, 420px)' }}
                maxW="420px"
                position={{ base: 'static', xl: 'fixed' }}
                top={{ xl: '8px' }}
                left={{ xl: '50%' }}
                transform={{ xl: 'translateX(-50%)' }}
                zIndex={16}
              >
                <PatientSearch />
              </Box>
            </Flex>
          )}
          {role === 'secretary' && <Box flex={1} />}

          <HStack spacing={1} flex="0 0 auto">
            {headerActions}
            <Button
              size="sm"
              variant="ghost"
              px={2}
              aria-label={
                locale === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'
              }
              onClick={() => {
                setLocale(locale === 'en-us' ? 'ar' : 'en-us')
              }}
            >
              {locale === 'ar' ? 'EN' : 'ع'}
            </Button>
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                aria-label={
                  fullName ||
                  formatMessage({
                    id: 'navbar.profile',
                    defaultMessage: 'Profile'
                  })
                }
                icon={<Avatar name={fullName} src={photoURL} size="xs" />}
                variant="ghost"
                size="sm"
              />
              <MenuList>
                {role === 'doctor' && (
                  <>
                    <MenuItem as={Link} to="/settings">
                      {formatMessage({
                        id: 'navbar.settings',
                        defaultMessage: 'Settings'
                      })}
                    </MenuItem>
                    <MenuItem
                      as="a"
                      href="https://sijilo.featurebase.app/"
                      target="_blank"
                    >
                      {formatMessage({
                        id: 'navbar.giveFeedback',
                        defaultMessage: 'Give feedback'
                      })}
                    </MenuItem>
                    <MenuDivider />
                  </>
                )}
                <MenuItem color="red.600" onClick={logoutUser}>
                  {formatMessage({
                    id: 'navbar.logout',
                    defaultMessage: 'Log out'
                  })}
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        <Box
          as="main"
          flex={1}
          w="full"
          maxW={maxW}
          mx="auto"
          px={
            contentMode === 'full'
              ? 0
              : isWorkspace
                ? { base: 2, md: 4 }
                : { base: 4, md: 6 }
          }
          py={
            contentMode === 'full'
              ? 0
              : isWorkspace
                ? { base: 2, md: 3 }
                : { base: 5, md: 7 }
          }
          h={isWorkspace ? 'calc(100dvh - 48px)' : undefined}
          overflow={isWorkspace ? 'hidden' : undefined}
          minW={0}
        >
          {children}
        </Box>
      </Flex>
    </Flex>
  )
}
